import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useBasePath } from '@/providers/BasePathProvider';
import { usePersonalizedProducts } from '@/hooks/usePersonalizedProducts';
import { Product } from '@/lib/Apis';
import { ProductCard } from '@/components/ProductCard';
import { Skeleton } from '@/components/ui/Skeleton';
import { ArrowDown, Check } from 'lucide-react';
import { ProductFilters } from '@/lib/types';

interface RecommendedProductsSectionProps {
    title?: string;
    subtitle?: string;
    initialLimit?: number;
    increment?: number;
    filters?: ProductFilters;
    className?: string;
}

export function RecommendedProductsSection({
    title = "You may also like",
    subtitle = "Popular products selected for you",
    initialLimit = 5,
    increment = 5,
    filters,
    className = "",
}: RecommendedProductsSectionProps) {
    const router = useRouter();
    const { buildPath } = useBasePath();
    const [limit, setLimit] = useState(initialLimit);
    const [hasMore, setHasMore] = useState(true);

    const {
        products,
        isLoading
    } = usePersonalizedProducts({
        limit,
        filters,
        personalizedRatio: 70,
    });

    // Determine if we've reached the end of the available products
    useEffect(() => {
        if (!isLoading && products.length < limit) {
            setHasMore(false);
        } else {
            setHasMore(true);
        }
    }, [products.length, limit, isLoading]);

    const handleLoadMore = () => {
        setLimit((prev) => prev + increment);
    };

    if (!isLoading && products.length === 0) {
        return null; // Don't show section if no recommendations at all
    }

    return (
        <div className={`max-w-[1400px] mx-auto px-4 md:px-8 pb-20 ${className}`}>
            <div className="pt-16 border-t border-gray-100">
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-hsecondary">{title}</h2>
                    {subtitle && <p className="text-hmuted mt-1">{subtitle}</p>}
                </div>

                {isLoading && products.length === 0 ? (
                    <div className="grid grid-cols-2 gap-4 md:gap-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                        {[...Array(initialLimit)].map((_, index) => (
                            <div
                                key={index}
                                className="aspect-[4/5] animate-pulse rounded-2xl border border-slate-200 bg-slate-50"
                            />
                        ))}
                    </div>
                ) : (
                    <>
                    <div className="grid min-[300px]:grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                            {products.map((product) => (
                                <ProductCard
                                    key={product._id || product.id}
                                    product={product as Product}
                                    onClickProduct={(p) => router.push(buildPath(`/products/${p._id}`))}
                                />
                            ))}

                            {/* Show skeletons for loading more items */}
                            {isLoading && products.length > 0 && (
                                [...Array(increment)].map((_, index) => (
                                    <div
                                        key={`loading-more-${index}`}
                                        className="aspect-[4/5] animate-pulse rounded-2xl border border-slate-200 bg-slate-50"
                                    />
                                ))
                            )}
                        </div>

                        <div className="flex justify-center">
                            {hasMore ? (
                                <button
                                    onClick={handleLoadMore}
                                    disabled={isLoading}
                                    className="group px-8 py-3 bg-white border border-gray-200 hover:border-hprimary hover:text-hprimary text-gray-700 font-medium rounded-xl transition-all shadow-sm hover:shadow-md flex items-center gap-2"
                                >
                                    {isLoading ? 'Loading...' : 'Show more products'}
                                    {!isLoading && <ArrowDown className="h-4 w-4 group-hover:translate-y-1 transition-transform" />}
                                </button>
                            ) :null}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
